import React, { useMemo, useEffect, useCallback, useState } from 'react';
import MakePayment from '../../../../external-components/payments/MakePayment';
import Terms from './Terms';
import EditRecurringPaymentInformation from './EditRecurringPaymentInformation';
import SinglePaymentInformation from '../../../../external-components/payments/steps/SinglePaymentInformation';
import RecurringPaymentConfirmation from './RecurringPaymentConfirmation';

const RecurringPayment = (props) => {
	const { data, paymentActions, payment, defaultEmailAddress } = props;
	const { policyId, policyNumber, paymentPlanId, isAdd,userId, paymentPlan } = data;
	
	const [loading, setLoading] = useState(false);
	const [hasExisting, setHasExisting] = useState(false);
	const [source, setSource] = useState(null);

	useEffect(() => {
		const load = async () => {
			try {
				setLoading(true);
				const { response: recurring } = await paymentActions.getRecurringPayment(policyNumber, policyId, userId);
			if (recurring.hasExisting) {
					setSource({ ...recurring.source, emailAddress: recurring.source.emailAddress || defaultEmailAddress });
					setHasExisting(recurring.hasExisting);
				}

				setLoading(false);
			} catch {
				setLoading(false);
			}
		};

		if (!isAdd) {
			load();
		}
	}, [defaultEmailAddress, isAdd, paymentActions, paymentPlanId, policyId, policyNumber,userId]);

	const policies = useMemo(() => [{ policyNumber, policyId, paymentPlanId, paymentPlan }], [policyId, policyNumber, paymentPlanId, paymentPlan]);

	const _payment = useMemo(() => {
		const settings = { ...props.payment.settings };
		settings.allowSplitPayments = false;
		return { ...props.payment, settings };
	}, [props.payment]);

	const [alterPaymentMethod, setAlterPaymentMethod] = useState(false);

	const onPaymentInformationUpdate = useCallback((source) => {
		setSource(source);
	}, []);

	const termsStep = useCallback(() => {
		return {
			subheader: 'Agreement Terms',
			content: <Terms sources={[source]} />,
		};
	}, [source]);

	const onChangePaymentMethod = useCallback(() => {
		setSource({ type: 'check', emailAddress: defaultEmailAddress });
		setAlterPaymentMethod(true);
	}, [defaultEmailAddress]);

	const editRecurringPaymentInformationStep = useCallback(
		({ payment, onCancel }) => {
			const isCredit = source ? source.type === 'credit' : false;

			return {
				subheader: 'Update Recurring Payment Information',
				alternateContinueButtonText: isCredit ? 'Update' : 'Close',
				content: <EditRecurringPaymentInformation settings={payment.settings} hasExisting={hasExisting} source={source} policyNumber={policyNumber} onChangePaymentMethod={onChangePaymentMethod} onPaymentInformationUpdate={onPaymentInformationUpdate} isCredit={isCredit} />,
				getErrors: () => {
					const errors = [];

					if (!source || !source.emailAddress || source.emailAddress === '') {
						errors.push('Email Address is required');
					}

					return errors;
				},
				onContinue: isCredit ? null : onCancel,
				showCancel: () => isCredit,
			};
		},
		[hasExisting, source, policyNumber, onChangePaymentMethod, onPaymentInformationUpdate]
	);

	const newRecurringPaymentInformationStep = useCallback(
		({ payment, singlePaymentRef, step, setStep }) => {
			return {
				subheader: 'Enter New Recurring Payment Information',
				content: <SinglePaymentInformation ref={singlePaymentRef} source={source} isRecurring onPaymentInformationUpdate={setSource} payment={payment} actions={paymentActions} onContinue={() => setStep(step + 1)} defaultEmailAddress={defaultEmailAddress} policies={policies} hasRecurringPaymentSetup={data?.billing?.hasRecurringPaymentSetup} />,
				onContinue: () => singlePaymentRef.current.continue(),
				getErrors: (context) => {
					const errors = [];

					if (!source || !source.emailAddress || source.emailAddress === '') {
						errors.push('Email Address is required');
					}

					if (context.singlePaymentRef.current && !context.singlePaymentRef.current.isValid()) {
						errors.push('Payment Information is not valid');
					}

					return errors;
				},
			};
		},
		[defaultEmailAddress, paymentActions, policies, source]
	);

	const recurringPaymentConfirmationStep = useCallback(
		({ setLoading, loading }) => {
			return {
				subheader: null,
				content: <RecurringPaymentConfirmation source={source} payment={payment} actions={paymentActions} data={data} setLoading={setLoading} loading={loading} />,
				showBack: () => false,
				showCancel: () => false,
				alternateContinueButtonText: 'Close',
			};
		},
		[data, payment, paymentActions, source]
	);

	const steps = useMemo(() => {
		if (hasExisting && !alterPaymentMethod) {
			return [editRecurringPaymentInformationStep, termsStep, recurringPaymentConfirmationStep];
		} else {
			return [newRecurringPaymentInformationStep, termsStep, recurringPaymentConfirmationStep];
		}
	}, [alterPaymentMethod, editRecurringPaymentInformationStep, hasExisting, newRecurringPaymentInformationStep, recurringPaymentConfirmationStep, termsStep]);

	return (
		<React.Fragment>
			<MakePayment {...props} payment={_payment} steps={steps} policies={policies} isRecurring loading={loading} />;
		</React.Fragment>
	);
};

export default RecurringPayment;
